import React, { useEffect, useRef, useState } from "react";
import { useAutoplayVideo } from "@/core/utils/autoplay-videos";
import { generateLocalSEOMeta } from "./localseo.meta";
import { generateBlogRichSnippets } from "./google-rich-snippet";
import BlogBreadcrumbs from "./BlogBreadcrumbs";
import BlogSidebar from "./BlogSidebar";
import blogDataDC from "@/core/Blogs/data/DC/database_content_updated-dc.json";
import Hero from "./Hero";
import BlogGallery from "./BlogGallery";
import Blockquote from "./Blockquote";
import TagsSection from "./TagsSection";
import SocialShare from "./SocialShare";
import BlogMeta from "./BlogMeta";
import { GoogleMapsDirection } from "./GoogleMapsDirection";
import OpeningHours from "./OpeninHours";

interface DataItem {
  subject: string;
  city: string;
  state: string;
}

interface BlogContent {
  metaDescription: string;
  title: string;
  introParagraph: string;
  middleParagraph: string;
  conclusionParagraph: string;
  dataItem: DataItem;
  sk?: string;
}

interface RelatedPost {
  title: string;
  slug: string;
  imageUrl?: string;
}

const LocalSEO: React.FC<BlogContent> = ({
  metaDescription,
  title,
  introParagraph,
  middleParagraph,
  conclusionParagraph,
  dataItem,
  sk,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [relatedPosts, setRelatedPosts] = useState<RelatedPost[]>([]);

  // Generate meta information for this specific page
  const META = generateLocalSEOMeta(
    dataItem.subject,
    dataItem.city,
    dataItem.state,
    metaDescription,
    title
  );

  // Generate rich snippets for better SEO
  const richSnippets = generateBlogRichSnippets(
    dataItem,
    title,
    metaDescription
  );

  // Use the autoplay video hook
  useAutoplayVideo(videoRef, { muted: true, playbackRate: 1 });

  // Find related posts based on subject, city, or state
  useEffect(() => {
    try {
      if (!Array.isArray(blogDataDC)) {
        console.error("Blog data is not an array");
        return;
      }

      // Filter out invalid blogs and the current blog
      const validBlogs = (blogDataDC as any[]).filter(
        (blog) => blog && blog.sk && blog.title && blog.sk !== sk
      );

      // Find related posts by matching subject, city, or state
      const matchingPosts = validBlogs.filter((blog) => {
        if (blog.subject === dataItem.subject) return true;
        if (blog.city === dataItem.city) return true;
        if (blog.state === dataItem.state) return true;
        return false;
      });

      // Convert to RelatedPost format and limit to 3 posts
      const formattedRelatedPosts: RelatedPost[] = matchingPosts
        .slice(0, 3)
        .map((blog) => ({
          title: blog.title,
          slug: blog.sk,
          imageUrl:
            "/images/website/street-lawyer-services-weed-dispensary-dc-75.jpg",
        }));

      setRelatedPosts(formattedRelatedPosts);
    } catch (error) {
      console.error("Error loading related posts:", error);
    }
  }, [dataItem, sk]);

  // Add reading progress bar
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const progressBar = document.createElement("div");
    progressBar.className =
      "progress-bar fixed top-0 left-0 h-1 bg-cannabis-green-500 z-50";
    progressBar.style.width = "0%";
    document.body.appendChild(progressBar);

    const updateProgress = () => {
      const totalHeight = content.scrollHeight;
      const viewportHeight = window.innerHeight;
      const scrollTop = window.scrollY;
      const scrolled = (scrollTop / (totalHeight - viewportHeight)) * 100;
      progressBar.style.width = `${Math.min(scrolled, 100)}%`;
    };

    window.addEventListener("scroll", updateProgress);
    return () => {
      window.removeEventListener("scroll", updateProgress);
      document.body.removeChild(progressBar);
    };
  }, []);

  // Format the paragraphs to improve readability
  const formatParagraph = (text: string) => {
    if (!text) return "";
    return text.split("\n").map((paragraph, i) => (
      <p key={i} className="mb-4 text-center md:text-left">
        {paragraph}
      </p>
    ));
  };

  // Generate local business schema
  const localBusinessSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Street Lawyer Services DC",
    description: META.meta_description,
    url: META.canonicalUrl,
    address: {
      "@type": "PostalAddress",
      streetAddress: "3400 Connecticut Avenue NW",
      addressLocality: "Washington",
      addressRegion: "DC",
      postalCode: "20008",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "38.936193",
      longitude: "-77.070396",
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ],
      opens: "10:00",
      closes: "20:00",
    },
    servesCuisine: ["Cannabis", "Marijuana", dataItem.subject],
    areaServed: {
      "@type": "City",
      name: dataItem.city,
      containedIn: {
        "@type": "State",
        name: dataItem.state === "DC" ? "District of Columbia" : dataItem.state,
      },
    },
    priceRange: "$$",
  });

  return (
    <div
      className="gradient-anime-sea-blue min-h-screen pb-16"
      ref={contentRef}
    >
      {/* SEO Meta and Structured Data */}
      <BlogMeta
        title={META.title}
        metaDescription={META.meta_description}
        keywords={META.keywords}
        canonicalUrl={META.canonicalUrl}
        ogImage={META.ogImage}
        articleSchema={richSnippets.articleSchema}
        dispensaryServiceSchema={richSnippets.dispensaryServiceSchema}
        breadcrumbSchema={richSnippets.breadcrumbSchema}
        localBusinessSchema={localBusinessSchema}
      />

      {/* Hero Video */}
      <Hero
        videoRef={videoRef}
        title={title}
        metaDescription={META.meta_description}
      />

      {/* Main Content with Sidebar */}
      <div className="w-11/12 max-w-6xl mx-auto mt-8" id="content">
        {/* Breadcrumb navigation */}
        <div className="mb-6">
          <BlogBreadcrumbs title={title} subject={dataItem.subject} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main content area */}
          <div className="lg:col-span-8">
            <div className="flex flex-col w-full gap-6 text-lg md:text-xl font-medium leading-relaxed text-white">
              <div className="prose prose-lg prose-invert max-w-none">
                {formatParagraph(introParagraph)}

                {/* Social sharing buttons */}
                <SocialShare url={META.canonicalUrl} title={title} />

                {/* Image Gallery */}
                <BlogGallery />

                {formatParagraph(middleParagraph)}

                {/* Pull quote */}
                <Blockquote city={dataItem.city} />

                {formatParagraph(conclusionParagraph)}

                {/* Tags section */}
                <TagsSection
                  subject={dataItem.subject}
                  city={dataItem.city}
                  state={dataItem.state}
                />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <BlogSidebar relatedPosts={relatedPosts} dataItem={dataItem} />
        </div>
      </div>

      <div className="w-11/12 max-w-6xl mx-auto py-12">
        <h2 className="text-3xl font-bold text-center mb-8 text-white">
          Welcome to Street Lawyer Services
        </h2>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Hours of Operation - Takes 1/3 width on desktop */}
          <div className="lg:w-1/3">
            <OpeningHours showHeading={false} className="p-0" />
          </div>

          {/* Google Maps Direction - Takes 2/3 width on desktop */}
          <div className="lg:w-2/3">
            <GoogleMapsDirection
              subject={dataItem.subject}
              city={dataItem.city}
              state={dataItem.state}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocalSEO;
